// @ts-strict-ignore
import Icon from '@ant-design/icons';
import Skeleton from 'antd/lib/skeleton';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { displayStatValue, isStatTrendValueSignificant } from '../../helpers';
import {
    AppInfo,
    DataPointType,
    OrgOverviewStatistic,
    ReleaseStepType,
    StatisticSampleBucketDataPoint,
    StatisticScope,
    StatisticTimeSeriesDataPoint,
    StatisticType,
    StatisticUnit,
    TargetDateSuccessRate,
    TargetDateSuccessRateStepKey,
} from '../../types';
import { ChartColors, useChartColors } from '../styles/themeMode';
import { CustomTooltipContent, RECHARTS_DEFAULT_SERIES_COLORS } from './OrgOverviewRecharts';
import { archetypeToSummaryViewProperty, getSummaryViewPropertyLabel } from '@/helpers/summaryViewHelpers';
import { RootState } from '../../reducers/reducers';
import { selectAllAppNamesAndPlatformsByAppId } from '../../selectors/selectors';
import { selectDestinationsData } from '../../selectors/summaryViewSelectors';
import { antGrey } from '../styles/globalStyles';
import {
    GetAppNameWithPlatform,
    GetResolvedBarSize,
    colorForStatCategory,
    displayOrgOverviewStatisticLabel,
    iconForStatCategory,
    tooltipForScope,
} from './orgOverviewHelpers';
import { StatSection, arrowForTrend, getTrendingTooltip } from './OrgOverviewStatCards';
import { useSelector } from 'react-redux';
import { isNil } from 'lodash';

interface TargetDateSuccessRateGraphDatum {
    label: string;
    kickoffSuccessRate: number;
    submissionSuccessRate: number;
    releaseSuccessRate: number;
}

interface TargetDateSuccessRateGroupedDatum {
    step: string;
    [seriesLabel: string]: string | number | undefined;
}

interface OrgOverviewTargetDateSuccessRateBarGraphProps {
    data: TargetDateSuccessRateGraphDatum[];
    type: StatisticType;
    unit: StatisticUnit;
    chartColors: ChartColors;
}

interface TargetDateSuccessRateAggregate {
    key: string;
    label: string;
    kickoffSuccessRate?: number;
    submissionSuccessRate?: number;
    releaseSuccessRate?: number;
    kickoffCount: number;
    submissionCount: number;
    releaseCount: number;
}

interface StepChartDatum {
    label: string;
    value: number;
}

interface StepAggregate {
    key: string;
    label: string;
    successRate?: number;
    count?: number;
}

interface OrgOverviewTargetDateSuccessRateStatProps extends OrgOverviewStatistic {
    isLoading?: boolean;
    orgId: string;
    scope: StatisticScope;
}

interface OrgOverviewStepTargetDateSuccessRateStatProps extends OrgOverviewStatistic {
    type:
        | StatisticType.TargetDateSuccessRateKickoff
        | StatisticType.TargetDateSuccessRateSubmission
        | StatisticType.TargetDateSuccessRateRelease;
    isLoading?: boolean;
    orgId: string;
    scope: StatisticScope;
}

const getAppLabel = (appNamesAndPlatformsByAppId: Record<string, AppInfo>, appId?: string) => {
    return appId && appNamesAndPlatformsByAppId[appId]?.name ?
            GetAppNameWithPlatform(appNamesAndPlatformsByAppId[appId].name, appNamesAndPlatformsByAppId[appId].platform)
        :   'Unknown App';
};

const getTargetDateSuccessRateLabel = (
    dataPoint: DataPointType,
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    destinationNamesById: Record<string, string>,
): string => {
    if (dataPoint.destinationId) {
        const appLabel = getAppLabel(appNamesAndPlatformsByAppId, dataPoint.appId);
        const destinationLabel = destinationNamesById[dataPoint.destinationId] ?? dataPoint.destinationId;

        return destinationLabel ? destinationLabel : appLabel;
    }

    return getAppLabel(appNamesAndPlatformsByAppId, dataPoint.appId);
};

const datumValue = (datum: TargetDateSuccessRateGraphDatum | undefined, key: string): number | undefined => {
    return datum?.[key as keyof TargetDateSuccessRateGraphDatum] as number | undefined;
};

export const OrgOverviewTargetDateSuccessRateStat: React.FC<OrgOverviewTargetDateSuccessRateStatProps> = ({
    type,
    unit,
    trend,
    trendStatus,
    category,
    displayText,
    description,
    dataPoints,
    isLoading,
    orgId,
    scope,
}) => {
    const chartColors = useChartColors();
    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const title = displayText || displayOrgOverviewStatisticLabel(type);
    const tooltip = description || tooltipForScope(scope, type);
    const destinationNamesById = React.useMemo(() => {
        return (orgDestinationsData?.appDestinations ?? []).reduce<Record<string, string>>((acc, appData) => {
            (appData.destinations ?? []).forEach((destination) => {
                acc[destination.stepConfigId] = destination.name;
                if (destination.destinationId) {
                    acc[destination.destinationId] = destination.name;
                }
            });
            return acc;
        }, {});
    }, [orgDestinationsData]);
    const data = React.useMemo(() => {
        const aggregatesByKey = (dataPoints as StatisticSampleBucketDataPoint[]).reduce<
            Record<string, TargetDateSuccessRateAggregate>
        >((acc, dataPoint) => {
            const value = dataPoint.value as TargetDateSuccessRate;
            const key =
                dataPoint.destinationId ? `destination:${dataPoint.destinationId}`
                : dataPoint.appId ? `app:${dataPoint.appId}`
                : 'unknown';

            if (!acc[key]) {
                acc[key] = {
                    key,
                    label: getTargetDateSuccessRateLabel(dataPoint, appNamesAndPlatformsByAppId, destinationNamesById),
                    kickoffCount: 0,
                    submissionCount: 0,
                    releaseCount: 0,
                };
            }

            if (Number.isFinite(value.kickoffSuccessRate)) {
                acc[key].kickoffSuccessRate = (acc[key].kickoffSuccessRate ?? 0) + value.kickoffSuccessRate;
                acc[key].kickoffCount += 1;
            }

            if (Number.isFinite(value.submissionSuccessRate)) {
                acc[key].submissionSuccessRate = (acc[key].submissionSuccessRate ?? 0) + value.submissionSuccessRate;
                acc[key].submissionCount += 1;
            }

            if (Number.isFinite(value.releaseSuccessRate)) {
                acc[key].releaseSuccessRate = (acc[key].releaseSuccessRate ?? 0) + value.releaseSuccessRate;
                acc[key].releaseCount += 1;
            }

            return acc;
        }, {});

        return Object.values(aggregatesByKey)
            .map((aggregate) => ({
                label: aggregate.label,
                kickoffSuccessRate:
                    aggregate.kickoffCount ? aggregate.kickoffSuccessRate / aggregate.kickoffCount : undefined,
                submissionSuccessRate:
                    aggregate.submissionCount ? aggregate.submissionSuccessRate / aggregate.submissionCount : undefined,
                releaseSuccessRate:
                    aggregate.releaseCount ? aggregate.releaseSuccessRate / aggregate.releaseCount : undefined,
            }))
            .sort((left, right) => left.label.localeCompare(right.label));
    }, [appNamesAndPlatformsByAppId, dataPoints, destinationNamesById]);

    const shouldShowSecondaryValue = !isNil(trend);
    const isTrendSignificant = isStatTrendValueSignificant(trend, type);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                gridColumn: 'auto / span 3',
                alignItems: 'stretch',
            }}
        >
            {isLoading ?
                <Skeleton
                    active
                    round={false}
                    style={{
                        padding: 12,
                    }}
                    paragraph={{
                        rows: 3,
                    }}
                />
            :   <>
                    <div
                        style={{
                            height: 56,
                            flexShrink: 0,
                        }}
                    >
                        <StatSection
                            title={title}
                            tooltip={tooltip}
                            secondaryValueIcon={
                                shouldShowSecondaryValue && isTrendSignificant && trendStatus ?
                                    arrowForTrend(trend, trendStatus, 14)
                                :   null
                            }
                            trendingTooltip={
                                shouldShowSecondaryValue && isTrendSignificant ?
                                    getTrendingTooltip(
                                        type,
                                        title,
                                        trend,
                                        displayStatValue(type, trend, unit),
                                        dataPoints,
                                    )
                                :   ''
                            }
                            cardIcon={
                                <Icon
                                    component={iconForStatCategory(category)}
                                    style={{
                                        fontSize: 14,
                                        color: colorForStatCategory(category),
                                    }}
                                />
                            }
                        />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 200,
                            backgroundColor: 'var(--rw-chart-container-bg)',
                        }}
                    >
                        <OrgOverviewTargetDateSuccessRateBarGraph
                            data={data}
                            type={type}
                            unit={unit}
                            chartColors={chartColors}
                        />
                    </div>
                </>
            }
        </div>
    );
};

const TargetDateSuccessRateStatisticToStepInfo: Record<
    | StatisticType.TargetDateSuccessRateKickoff
    | StatisticType.TargetDateSuccessRateSubmission
    | StatisticType.TargetDateSuccessRateRelease,
    { key: TargetDateSuccessRateStepKey; archetype: ReleaseStepType }
> = {
    [StatisticType.TargetDateSuccessRateKickoff]: {
        key: TargetDateSuccessRateStepKey.KickoffSuccessRate,
        archetype: ReleaseStepType.Kickoff,
    },
    [StatisticType.TargetDateSuccessRateSubmission]: {
        key: TargetDateSuccessRateStepKey.SubmissionSuccessRate,
        archetype: ReleaseStepType.Submission,
    },
    [StatisticType.TargetDateSuccessRateRelease]: {
        key: TargetDateSuccessRateStepKey.ReleaseSuccessRate,
        archetype: ReleaseStepType.Takeoff,
    },
};

const OrgOverviewTargetDateSuccessRateBarGraph: React.FC<OrgOverviewTargetDateSuccessRateBarGraphProps> = ({
    data,
    type,
    unit,
    chartColors,
}) => {
    const steps = Object.values(TargetDateSuccessRateStatisticToStepInfo).map(({ key, archetype }) => ({
        key,
        label: getSummaryViewPropertyLabel(archetypeToSummaryViewProperty(archetype)),
    }));

    const visibleSteps = React.useMemo(
        () =>
            steps.filter((category) =>
                data.some((datum) =>
                    Number.isFinite(datum[category.key as keyof TargetDateSuccessRateGraphDatum] as number),
                ),
            ),
        [data, steps],
    );
    const visibleSeries = React.useMemo(
        () =>
            data
                .map((datum) => datum.label)
                .filter((label) =>
                    visibleSteps.some((step) =>
                        Number.isFinite(
                            datumValue(
                                data.find((datum) => datum.label === label),
                                step.key,
                            ),
                        ),
                    ),
                ),
        [data, visibleSteps],
    );
    const groupedData = React.useMemo<TargetDateSuccessRateGroupedDatum[]>(
        () =>
            visibleSteps.map((step) => {
                const presentValues = data
                    .map((datum) => ({
                        label: datum.label,
                        value: datumValue(datum, step.key),
                    }))
                    .filter((item): item is { label: string; value: number } => Number.isFinite(item.value));
                const divisor = presentValues.length || 1;

                return presentValues.reduce(
                    (acc, item) => {
                        acc[item.label] = item.value / divisor;
                        acc[`raw__${item.label}`] = item.value;
                        return acc;
                    },
                    {
                        step: step.label,
                    } as TargetDateSuccessRateGroupedDatum,
                );
            }),
        [data, visibleSteps],
    );

    if (!data?.length || !visibleSteps?.length || !visibleSeries?.length) {
        return null;
    }

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={groupedData}
                margin={{
                    top: 12,
                    right: 20,
                    bottom: 20,
                    left: 20,
                }}
                barGap={8}
                barSize={GetResolvedBarSize(visibleSteps)}
            >
                <CartesianGrid stroke={chartColors.gridLine} vertical={false} />
                <XAxis
                    dataKey="step"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                />
                <YAxis
                    domain={[0, 100]}
                    tickLine={false}
                    axisLine={false}
                    tickCount={5}
                    tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                    tickMargin={6}
                    tickFormatter={(value) => displayStatValue(type, Number(value), unit)}
                />
                <Tooltip
                    wrapperStyle={{ zIndex: 10000 }}
                    cursor={{ fill: chartColors.tooltipBg, fillOpacity: 0.1 }}
                    content={
                        <CustomTooltipContent
                            chartColors={chartColors}
                            labelFormatter={(label) => String(label)}
                            formatter={(value, name) => [displayStatValue(type, Number(value), unit), name]}
                            sortPayload={(payload) => payload}
                            itemsRenderer={(payload) => {
                                const row = payload?.[0]?.payload ?? {};

                                return visibleSeries
                                    .map((seriesLabel, index) => ({
                                        seriesLabel,
                                        rawValue: row[`raw__${seriesLabel}`],
                                        color: RECHARTS_DEFAULT_SERIES_COLORS[
                                            index % RECHARTS_DEFAULT_SERIES_COLORS.length
                                        ],
                                    }))
                                    .filter((item) => Number.isFinite(Number(item.rawValue)))
                                    .map((item) => (
                                        <div
                                            key={item.seriesLabel}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 12,
                                                color: chartColors.tooltipText,
                                                fontSize: 12,
                                                marginTop: 4,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: 999,
                                                        backgroundColor: item.color,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <span>{item.seriesLabel}</span>
                                            </div>
                                            <span>{displayStatValue(type, Number(item.rawValue), unit)}</span>
                                        </div>
                                    ));
                            }}
                        />
                    }
                />
                {visibleSeries.map((seriesLabel, index) => (
                    <Bar
                        key={seriesLabel}
                        dataKey={seriesLabel}
                        stackId="value"
                        name={seriesLabel}
                        fill={RECHARTS_DEFAULT_SERIES_COLORS[index % RECHARTS_DEFAULT_SERIES_COLORS.length]}
                        isAnimationActive={false}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
};

export const OrgOverviewStepTargetDateSuccessRateStat: React.FC<OrgOverviewStepTargetDateSuccessRateStatProps> = ({
    type,
    unit,
    trend,
    trendStatus,
    category,
    displayText,
    description,
    dataPoints,
    isLoading,
    orgId,
    scope,
}) => {
    const chartColors = useChartColors();
    const { key: stepKey } = TargetDateSuccessRateStatisticToStepInfo[type] || {};
    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const tooltip = description || tooltipForScope(scope, type);
    const destinationNamesById = React.useMemo(() => {
        return (orgDestinationsData?.appDestinations ?? []).reduce<Record<string, string>>((acc, appData) => {
            (appData.destinations ?? []).forEach((destination) => {
                acc[destination.stepConfigId] = destination.name;
                if (destination.destinationId) {
                    acc[destination.destinationId] = destination.name;
                }
            });
            return acc;
        }, {});
    }, [orgDestinationsData]);

    const data = React.useMemo<StepChartDatum[]>(() => {
        const aggregatesByKey = (dataPoints as StatisticTimeSeriesDataPoint[]).reduce<Record<string, StepAggregate>>(
            (acc, dataPoint) => {
                const aggregateKey =
                    dataPoint.destinationId ? `destination:${dataPoint.destinationId}`
                    : dataPoint.appId ? `app:${dataPoint.appId}`
                    : 'unknown';

                if (!acc[aggregateKey]) {
                    acc[aggregateKey] = {
                        key: aggregateKey,
                        label: getTargetDateSuccessRateLabel(
                            dataPoint,
                            appNamesAndPlatformsByAppId,
                            destinationNamesById,
                        ),
                        count: 0,
                    };
                }

                if (Number.isFinite(dataPoint.value)) {
                    acc[aggregateKey].successRate = (acc[aggregateKey].successRate ?? 0) + dataPoint.value;
                    acc[aggregateKey].count += 1;
                }

                return acc;
            },
            {},
        );

        return Object.values(aggregatesByKey)
            .map((aggregate) => {
                const valueForStep = aggregate.count ? aggregate.successRate / aggregate.count : undefined;

                return {
                    label: aggregate.label,
                    value: valueForStep,
                };
            })
            .filter((item): item is StepChartDatum => Number.isFinite(item.value))
            .sort((left, right) => right.value - left.value);
    }, [appNamesAndPlatformsByAppId, dataPoints, destinationNamesById]);

    const shouldShowSecondaryValue = !isNil(trend);
    const isTrendSignificant = isStatTrendValueSignificant(trend, type);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                alignItems: 'stretch',
            }}
        >
            {isLoading ?
                <Skeleton active round={false} style={{ padding: 12 }} paragraph={{ rows: 3 }} />
            :   <>
                    <div style={{ height: 56, flexShrink: 0 }}>
                        <StatSection
                            title={displayText}
                            tooltip={tooltip}
                            secondaryValueIcon={
                                shouldShowSecondaryValue && isTrendSignificant && trendStatus ?
                                    arrowForTrend(trend, trendStatus, 14)
                                :   null
                            }
                            trendingTooltip={
                                shouldShowSecondaryValue && isTrendSignificant ?
                                    getTrendingTooltip(
                                        type,
                                        displayText,
                                        trend,
                                        displayStatValue(type, trend, unit),
                                        dataPoints,
                                    )
                                :   ''
                            }
                            cardIcon={
                                <Icon
                                    component={iconForStatCategory(category)}
                                    style={{
                                        fontSize: 14,
                                        color: colorForStatCategory(category),
                                    }}
                                />
                            }
                        />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 200,
                            backgroundColor: 'var(--rw-chart-container-bg)',
                        }}
                    >
                        <OrgOverviewStepTargetDateSuccessRateBarChart
                            data={data}
                            type={type}
                            unit={unit}
                            chartColors={chartColors}
                            stepKey={stepKey}
                        />
                    </div>
                </>
            }
        </div>
    );
};

const OrgOverviewStepTargetDateSuccessRateBarChart: React.FC<{
    data: StepChartDatum[];
    type: StatisticType;
    unit: StatisticUnit;
    chartColors: ChartColors;
    stepKey: TargetDateSuccessRateStepKey;
}> = ({ data = [], type, unit, chartColors, stepKey }) => {
    if (!data?.length) {
        return null;
    }

    const stepIndex = Object.values(TargetDateSuccessRateStepKey).indexOf(stepKey);

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={data}
                layout="horizontal"
                margin={{
                    top: 8,
                    right: 20,
                    bottom: 12,
                    left: 12,
                }}
                barSize={GetResolvedBarSize(data)}
            >
                <CartesianGrid stroke={chartColors.gridLine} horizontal />
                <XAxis
                    type="category"
                    dataKey="label"
                    width={120}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chartColors.axisLabel, fontSize: 0 }}
                />

                <YAxis
                    type="number"
                    domain={[0, 100]}
                    tickLine={false}
                    axisLine={false}
                    tickCount={5}
                    tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                    tickMargin={6}
                    tickFormatter={(value) => displayStatValue(type, Number(value), unit)}
                />
                <Tooltip
                    wrapperStyle={{ zIndex: 10000 }}
                    cursor={{ fill: chartColors.tooltipBg, fillOpacity: 0.1 }}
                    content={
                        <CustomTooltipContent
                            chartColors={chartColors}
                            formatter={(value) => [displayStatValue(type, Number(value), unit), 'Value:']}
                        />
                    }
                />
                <Bar
                    dataKey="value"
                    name=""
                    fill={RECHARTS_DEFAULT_SERIES_COLORS[stepIndex]}
                    isAnimationActive={false}
                />
            </BarChart>
        </ResponsiveContainer>
    );
};
